import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  nextStatusesFor,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type AuthenticatedUser,
  type InvoiceDetail,
  type PaymentMethod,
} from '@faktorplus/shared';
import {
  useApproveForPayment,
  useCompleteDocuments,
  useDeleteInvoice,
  useMarkPaid,
  useRecordOwnerApproval,
  useRegisterPayment,
  useRemovePayment,
  useReturnToManager,
  useSetInvoiceEmployees,
  useUpdateBookkeeping,
} from '../../api/invoices';
import { useEmployees } from '../../api/dictionaries';
import { AssignInvoiceForm } from './assign-invoice-form';
import { Button } from '../../components/ui/button';
import { AlertIcon } from '../../components/ui/icons';
import { Field, Input, Select } from '../../components/ui/form';
import { ErrorBox } from '../../components/ui/feedback';
import { Modal, modalStyles } from '../../components/ui/modal';
import { ApiError } from '../../lib/api-client';
import { formatDate, formatMoney } from '../../lib/format';
import styles from './invoice-detail.module.css';

function today(): string {
  return toDateInput(new Date());
}

function toDateInput(value: Date | string): string {
  return new Date(value).toISOString().slice(0, 10);
}

/**
 * Actions available to accounting and the owner on a single invoice.
 *
 * Which cards appear is derived from the shared state machine rather than from
 * a hand-written list of statuses, so a transition the API allows can never be
 * one the interface quietly hides.
 */
export function OfficeActions({
  invoice,
  user,
  onDone,
}: {
  invoice: InvoiceDetail;
  user: AuthenticatedUser;
  onDone: () => void;
}) {
  const cards = officeCards(invoice, user);
  if (!cards) return null;

  return (
    <>
      {cards.canOwnerApprove && <OwnerApprovalCard invoice={invoice} />}
      {cards.canBook && (
        <AssignCard invoice={invoice} onDone={onDone} primary={cards.primary === 'assign'} />
      )}
      {cards.needsProtocol && <CompleteDocumentsCard invoice={invoice} onDone={onDone} />}
      {cards.canApprove && (
        <ApproveForPaymentCard
          invoice={invoice}
          warnNoProtocol={cards.needsProtocol}
          withoutOwner={user.role === 'ACCOUNTANT'}
          onDone={onDone}
          primary={cards.primary === 'approve'}
        />
      )}
      {cards.canReturn && <ReturnToManagerCard invoice={invoice} onDone={onDone} />}
      {cards.canMarkPaid && (
        <MarkPaidCard invoice={invoice} onDone={onDone} primary={cards.primary === 'markPaid'} />
      )}
      <EmployeesCard invoice={invoice} />
      <BookkeepingCard invoice={invoice} />
      {cards.canDelete && <DeleteInvoiceCard invoice={invoice} />}
    </>
  );
}

/**
 * Whether this viewer has any office action here.
 *
 * The detail page reserves a 400px column for the action cards and has to know
 * before laying out whether anything will fill it — an empty reserved column is
 * just a hole next to the invoice.
 *
 * Office always has one: the bookkeeping card never goes away, because the
 * ledger position is written after everything else is over.
 */
export function hasOfficeActions(_invoice: InvoiceDetail, user: AuthenticatedUser): boolean {
  return user.role === 'ADMIN' || user.role === 'ACCOUNTANT';
}

/** The card that moves the invoice forward — highlighted so it reads first. */
type PrimaryCard = 'assign' | 'approve' | 'markPaid' | null;

interface OfficeCards {
  canBook: boolean;
  needsProtocol: boolean;
  canApprove: boolean;
  canReturn: boolean;
  canMarkPaid: boolean;
  canDelete: boolean;
  canOwnerApprove: boolean;
  primary: PrimaryCard;
}

/** `null` when the viewer is not office at all. */
function officeCards(invoice: InvoiceDetail, user: AuthenticatedUser): OfficeCards | null {
  if (user.role !== 'ADMIN' && user.role !== 'ACCOUNTANT') return null;

  const allowed = nextStatusesFor(invoice.status, user.role);
  const isReleased =
    invoice.status === 'APPROVED_FOR_PAYMENT' || invoice.status === 'PAID';

  // ASSIGNED is reachable both from NEW (first booking) and from
  // APPROVED_BY_MANAGER (owner sending it back) — different actions entirely.
  const canBook = allowed.includes('ASSIGNED') && invoice.status !== 'APPROVED_BY_MANAGER';
  const canApprove = allowed.includes('APPROVED_FOR_PAYMENT');
  const canMarkPaid = allowed.includes('PAID');

  return {
    canBook,
    needsProtocol: invoice.requiresHandoverProtocol && !invoice.documentsCompleteAt,
    canApprove,
    canReturn:
      allowed.includes('ASSIGNED') &&
      invoice.status === 'APPROVED_BY_MANAGER' &&
      invoice.allocations.some((allocation) => allocation.managerId !== null),
    canMarkPaid,
    // Mirrors `InvoicesService.assertMayDelete`: once the owner has released
    // the invoice for payment it is part of a payment decision, and only the
    // owner may erase one. Showing the button anyway would just be a 403.
    canDelete: user.role === 'ADMIN' || !isReleased,
    // The owner's "żeby mi to nie wisiało" (24.09.2026): sign off on a release
    // the accountant made in their place. Owner only, once.
    canOwnerApprove:
      user.role === 'ADMIN' &&
      invoice.approvedForPaymentWithoutOwner &&
      invoice.ownerApprovedAt === null,
    primary: primaryCard({ canBook, canApprove, canMarkPaid }, invoice.status),
  };
}

/**
 * Which card is the step the invoice is waiting for. Editing a booking that is
 * already with the manager is a correction, not a step, so ASSIGNED has none.
 */
function primaryCard(
  cards: { canBook: boolean; canApprove: boolean; canMarkPaid: boolean },
  status: InvoiceDetail['status'],
): PrimaryCard {
  if (cards.canApprove) return 'approve';
  if (cards.canMarkPaid) return 'markPaid';
  if (cards.canBook && (status === 'NEW' || status === 'DISPUTED')) return 'assign';
  return null;
}

function cardClass(primary: boolean): string {
  return primary ? `${styles.actionCard} ${styles.actionCardPrimary}` : styles.actionCard!;
}

/**
 * The card wording follows the invoice's state, so saving visibly changes
 * something: a form that reads "Przypisz fakturę" both before and after the
 * click leaves the user unsure whether anything happened.
 */
const ASSIGN_COPY = {
  NEW: { title: 'Przypisz fakturę', submit: 'Przypisz i wyślij' },
  DISPUTED: { title: 'Popraw przypisanie', submit: 'Wyślij ponownie' },
  ASSIGNED: { title: 'Zmień przypisanie', submit: 'Zapisz zmiany' },
} as const;

function AssignCard({
  invoice,
  onDone,
  primary,
}: {
  invoice: InvoiceDetail;
  onDone: () => void;
  primary: boolean;
}) {
  const copy = ASSIGN_COPY[invoice.status as keyof typeof ASSIGN_COPY] ?? ASSIGN_COPY.NEW;
  const lastDispute = invoice.statusHistory.find((entry) => entry.toStatus === 'DISPUTED');

  return (
    <div className={cardClass(primary)}>
      <p className={styles.actionTitle}>{copy.title}</p>

      {invoice.status === 'DISPUTED' && lastDispute?.comment && (
        <p className={styles.disputeNote}>
          {lastDispute.userName}: „{lastDispute.comment}"
        </p>
      )}

      {invoice.status === 'ASSIGNED' && (
        <p className={styles.actionNote}>
          Faktura czeka u kierownika. Zmiany wyślą ją ponownie do akceptacji.
        </p>
      )}

      <AssignInvoiceForm invoice={invoice} onSaved={onDone} submitLabel={copy.submit} />
    </div>
  );
}

/**
 * The owner's after-the-fact sign-off on a payment released without them.
 *
 * Tinted like the protocol card: it is a loose end to tie up, not a step of
 * the flow. Signing off clears the flag from every list and the dashboard
 * counter; the fact of the release stays in the summary and the history.
 */
function OwnerApprovalCard({ invoice }: { invoice: InvoiceDetail }) {
  const approve = useRecordOwnerApproval();
  const release = findReleaseWithoutOwner(invoice);

  return (
    <div className={`${styles.actionCard} ${styles.actionCardTinted}`}>
      <p className={styles.actionTitle}>Bez akceptacji właściciela</p>
      <p className={styles.actionNote}>
        {release
          ? `${release.userName} zatwierdziła zapłatę ${formatDate(release.createdAt)}, bez Twojej akceptacji.`
          : 'Księgowość zatwierdziła zapłatę bez Twojej akceptacji.'}
      </p>
      <p className={styles.actionNote}>
        Potwierdź, że o tym wiesz. Faktura zniknie z pulpitu, wpis w historii zostanie.
      </p>

      {approve.error && <ErrorBox>{errorMessage(approve.error)}</ErrorBox>}

      <Button
        variant="primary"
        isLoading={approve.isPending}
        onClick={() => approve.mutate(invoice.id)}
      >
        Potwierdzam zapłatę
      </Button>
    </div>
  );
}

/** The history entry that released the invoice in the owner's place, if any. */
function findReleaseWithoutOwner(invoice: InvoiceDetail) {
  return invoice.statusHistory.find(
    (entry) =>
      entry.toStatus === 'APPROVED_FOR_PAYMENT' &&
      (entry.comment ?? '').includes('bez akceptacji właściciela'),
  );
}

function CompleteDocumentsCard({
  invoice,
  onDone,
}: {
  invoice: InvoiceDetail;
  onDone: () => void;
}) {
  const [receivedOn, setReceivedOn] = useState(today());
  const [dueDate, setDueDate] = useState('');
  const complete = useCompleteDocuments();

  async function handleSubmit() {
    await complete.mutateAsync({
      id: invoice.id,
      input: {
        documentsCompleteAt: new Date(receivedOn),
        dueDate: dueDate ? new Date(dueDate) : null,
      },
    });
    onDone();
  }

  return (
    <div className={`${styles.actionCard} ${styles.actionCardTinted}`}>
      <p className={styles.actionTitle}>Protokół odbioru</p>
      <p className={styles.actionNote}>
        Zdejmuje z faktury znacznik „Brak protokołu”. Jeżeli zlecenie liczy termin od
        kompletu dokumentów, wpisz tu również nowy termin.
      </p>

      <Field label="Data wpływu protokołu">
        {(id) => (
          <Input
            id={id}
            type="date"
            value={receivedOn}
            onChange={(event) => setReceivedOn(event.target.value)}
          />
        )}
      </Field>

      <Field label="Nowy termin płatności">
        {(id) => (
          <Input
            id={id}
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        )}
      </Field>

      {complete.error && <ErrorBox>{errorMessage(complete.error)}</ErrorBox>}

      <Button variant="primary" isLoading={complete.isPending} onClick={() => void handleSubmit()}>
        Zapisz protokół
      </Button>
    </div>
  );
}

/**
 * The missing protocol is said out loud but no longer stops the owner. It is a
 * fact about the invoice they weigh, not a rule the system enforces — see
 * `InvoiceWorkflowService.approveForPayment`.
 */
function ApproveForPaymentCard({
  invoice,
  warnNoProtocol,
  withoutOwner,
  onDone,
  primary,
}: {
  invoice: InvoiceDetail;
  warnNoProtocol: boolean;
  /** The viewer is an accountant releasing in the owner's place. */
  withoutOwner: boolean;
  onDone: () => void;
  primary: boolean;
}) {
  const approve = useApproveForPayment();

  async function handleApprove() {
    await approve.mutateAsync({ invoiceIds: [invoice.id] });
    onDone();
  }

  return (
    <div className={cardClass(primary)}>
      <p className={styles.actionTitle}>Zatwierdzenie płatności</p>

      {withoutOwner && (
        <p className={styles.actionNote}>
          Zatwierdzasz bez akceptacji właściciela. Faktura dostanie trwały znacznik „Bez
          akceptacji właściciela”.
        </p>
      )}
      {warnNoProtocol && (
        <p className={styles.actionNote}>
          <AlertIcon className={styles.noteIcon} />
          Ta faktura czeka na protokół odbioru. Możesz ją zatwierdzić mimo to.
        </p>
      )}
      {approve.error && <ErrorBox>{errorMessage(approve.error)}</ErrorBox>}

      <Button
        variant="primary"
        large
        isLoading={approve.isPending}
        onClick={() => void handleApprove()}
      >
        Zatwierdź do zapłaty
      </Button>
    </div>
  );
}

function ReturnToManagerCard({ invoice, onDone }: { invoice: InvoiceDetail; onDone: () => void }) {
  const returnToManager = useReturnToManager();

  async function handleReturn() {
    await returnToManager.mutateAsync(invoice.id);
    onDone();
  }

  return (
    <div className={styles.actionCard}>
      <p className={styles.actionTitle}>Odesłać do kierownika?</p>
      <p className={styles.actionNote}>
        Faktura wróci do kierownika i zniknie z listy do zatwierdzenia.
      </p>

      {returnToManager.error && <ErrorBox>{errorMessage(returnToManager.error)}</ErrorBox>}

      <Button
        variant="danger"
        isLoading={returnToManager.isPending}
        onClick={() => void handleReturn()}
      >
        Odeślij do kierownika
      </Button>
    </div>
  );
}

/**
 * Records the money against the invoice.
 *
 * A cash invoice (or one already carrying its payment date) is only having its
 * flow closed — one date field, as before. A transfer takes an amount too:
 * pay less than what is left and the invoice becomes "częściowo zapłacona"
 * with a running balance (24.09.2026); cover the remainder and it turns PAID.
 */
function MarkPaidCard({
  invoice,
  onDone,
  primary,
}: {
  invoice: InvoiceDetail;
  onDone: () => void;
  primary: boolean;
}) {
  const closesFlowOnly = invoice.paymentMethod === 'CASH' || invoice.paidAt !== null;
  const [paidOn, setPaidOn] = useState(
    invoice.paidAt ? toDateInput(invoice.paidAt) : today(),
  );
  const [amount, setAmount] = useState(invoice.remainingAmount.toFixed(2));
  const markPaid = useMarkPaid();
  const registerPayment = useRegisterPayment();

  // Each recorded (or removed) transfer moves the balance; the field follows
  // it, so the next click always offers "the rest" by default.
  useEffect(() => {
    setAmount(invoice.remainingAmount.toFixed(2));
  }, [invoice.remainingAmount]);

  const parsedAmount = Number(amount);
  const settlesInFull =
    closesFlowOnly || Math.round(parsedAmount * 100) >= Math.round(invoice.remainingAmount * 100);
  const pending = markPaid.isPending || registerPayment.isPending;

  async function handleSubmit() {
    if (closesFlowOnly) {
      await markPaid.mutateAsync({ id: invoice.id, input: { paidAt: new Date(paidOn) } });
    } else {
      await registerPayment.mutateAsync({
        id: invoice.id,
        input: { amount: parsedAmount, paidAt: new Date(paidOn) },
      });
    }
    onDone();
  }

  return (
    <div className={cardClass(primary)}>
      <p className={styles.actionTitle}>Płatności</p>

      {invoice.paidAt && (
        <p className={styles.actionNote}>
          Zapłacona gotówką {formatDate(invoice.paidAt)}. Zamknięcie obiegu nie zmienia tej daty.
        </p>
      )}

      {!closesFlowOnly && (
        <>
          {invoice.paidAmount > 0 && (
            <p className={styles.actionNote}>
              Wpłacono już {formatMoney(invoice.paidAmount)}, zostało{' '}
              {formatMoney(invoice.remainingAmount)}.
            </p>
          )}

          <Field
            label="Kwota wpłaty"
            hint="Wpłata mniejsza niż kwota do zapłaty zostanie zapisana jako częściowa. Reszta zostaje w rozrachunkach."
          >
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            )}
          </Field>
        </>
      )}

      <Field label="Data zapłaty">
        {(id) => (
          <Input
            id={id}
            type="date"
            value={paidOn}
            onChange={(event) => setPaidOn(event.target.value)}
          />
        )}
      </Field>

      {(markPaid.error || registerPayment.error) && (
        <ErrorBox>{errorMessage(markPaid.error ?? registerPayment.error)}</ErrorBox>
      )}

      <Button
        variant="primary"
        isLoading={pending}
        disabled={!closesFlowOnly && !(parsedAmount > 0)}
        onClick={() => void handleSubmit()}
      >
        {settlesInFull ? 'Oznacz jako zapłaconą' : 'Zarejestruj wpłatę częściową'}
      </Button>

      {invoice.payments.length > 0 && <PaymentHistory invoice={invoice} />}
    </div>
  );
}

/**
 * The transfers recorded so far — the meeting's own example: a 300 tys.
 * invoice with 100 in September, 120 in October, 80 in November. A mis-keyed
 * row can be removed while the invoice is still open.
 */
/** How long the inline "Usunąć wpłatę?" stays open before folding back. */
const CONFIRM_TIMEOUT_MS = 5000;

function PaymentHistory({ invoice }: { invoice: InvoiceDetail }) {
  const remove = useRemovePayment();
  const canRemove = invoice.paidAt === null;
  // Two clicks to delete a booked amount, but no modal: the mistake being
  // undone is usually seconds old and still on screen.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  useEffect(() => {
    if (!confirmingId) return;
    const timer = window.setTimeout(() => setConfirmingId(null), CONFIRM_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [confirmingId]);

  return (
    <div className={styles.paymentHistory}>
      <p className={styles.actionNote}>Dotychczasowe wpłaty</p>
      {invoice.payments.map((payment) => (
        <div key={payment.id} className={styles.paymentRow}>
          <span>{formatDate(payment.paidAt)}</span>
          <span className={styles.paymentAmount}>{formatMoney(payment.amount)}</span>
          <span className={styles.paymentBy}>{payment.createdByName ?? '—'}</span>
          {canRemove && confirmingId !== payment.id && (
            <Button
              variant="ghost"
              aria-label={`Usuń wpłatę ${formatMoney(payment.amount)} z ${formatDate(payment.paidAt)}`}
              onClick={() => setConfirmingId(payment.id)}
            >
              Usuń
            </Button>
          )}
          {canRemove && confirmingId === payment.id && (
            <span className={styles.paymentConfirm} role="group" aria-label="Potwierdź usunięcie wpłaty">
              <span>Usunąć wpłatę {formatMoney(payment.amount)}?</span>
              <Button
                variant="danger"
                isLoading={remove.isPending && remove.variables?.paymentId === payment.id}
                onClick={() => {
                  void remove.mutateAsync({ id: invoice.id, paymentId: payment.id });
                  setConfirmingId(null);
                }}
              >
                Usuń
              </Button>
              <Button variant="ghost" onClick={() => setConfirmingId(null)}>
                Zostaw
              </Button>
            </span>
          )}
        </div>
      ))}
      {remove.error && <ErrorBox>{errorMessage(remove.error)}</ErrorBox>}
    </div>
  );
}

/**
 * Who the invoice was bought for — odzież robocza, paliwo, narzędzie dla
 * konkretnej osoby.
 *
 * Several people per invoice, without amounts: one bill for work clothing
 * covers the whole crew, and the client's rule is that naming them is enough
 * (3.09.2026). Open at every status — who the boots were for does not change
 * with the workflow, but is often remembered after the invoice has moved on.
 */
function EmployeesCard({ invoice }: { invoice: InvoiceDetail }) {
  const { data: employees = [] } = useEmployees();
  const [selected, setSelected] = useState<string[]>(() => invoice.employees.map((e) => e.id));
  const save = useSetInvoiceEmployees();

  const isDirty = !sameSet(selected, invoice.employees.map((employee) => employee.id));

  // A person taken off the list stays visible while still booked here, or the
  // card would quietly drop them on the next save.
  const options = [
    ...employees,
    ...invoice.employees
      .filter((booked) => !employees.some((employee) => employee.id === booked.id))
      .map((booked) => ({ ...booked, isActive: false })),
  ];

  function toggle(employeeId: string) {
    setSelected((current) =>
      current.includes(employeeId)
        ? current.filter((id) => id !== employeeId)
        : [...current, employeeId],
    );
  }

  return (
    <div className={styles.actionCard}>
      <p className={styles.actionTitle}>Pracownicy</p>

      {options.length === 0 ? (
        <p className={styles.actionNote}>
          Lista pracowników jest pusta. Dodaj osoby w Ustawieniach → Pracownicy.
        </p>
      ) : (
        <>
          <p className={styles.actionNote}>
            Dla kogo był ten zakup. Zostaw puste, jeśli faktura nie dotyczy konkretnych osób.
          </p>

          <div className={styles.employeeList}>
            {options.map((employee) => (
              <label key={employee.id} className={styles.employeeItem}>
                <input
                  type="checkbox"
                  checked={selected.includes(employee.id)}
                  onChange={() => toggle(employee.id)}
                />
                {employee.fullName}
                {!employee.isActive && <span className={styles.employeeInactive}>już nie pracuje</span>}
              </label>
            ))}
          </div>
        </>
      )}

      {save.error && <ErrorBox>{errorMessage(save.error)}</ErrorBox>}

      {options.length > 0 && (
        <Button
          variant="primary"
          disabled={!isDirty}
          isLoading={save.isPending}
          onClick={() =>
            void save.mutateAsync({ id: invoice.id, input: { employeeIds: selected } })
          }
        >
          Zapisz pracowników
        </Button>
      )}
    </div>
  );
}

/** Order is not meaningful here, so equality is about membership. */
function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value) => b.includes(value));
}

/**
 * What accounting writes down once the document has been booked.
 *
 * Present at every status, including on a paid invoice — that is the whole
 * point of the ledger position: it is filled in after the fact, so that
 * somebody holding a printout can find their way back to this screen.
 */
function BookkeepingCard({ invoice }: { invoice: InvoiceDetail }) {
  const [ledgerRef, setLedgerRef] = useState(invoice.ledgerRef ?? '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(invoice.paymentMethod);
  const [paidOn, setPaidOn] = useState(today());
  const save = useUpdateBookkeeping();

  // A cash invoice without a payment date yet: offer to record it here, so the
  // counters stop listing money that already left the drawer.
  const asksForPaidAt = paymentMethod === 'CASH' && invoice.paidAt === null;

  const isDirty =
    ledgerRef.trim() !== (invoice.ledgerRef ?? '') ||
    paymentMethod !== invoice.paymentMethod ||
    (asksForPaidAt && paidOn !== '');

  return (
    <div className={styles.actionCard}>
      <p className={styles.actionTitle}>Dane księgowe</p>

      <Field label="Pozycja księgowa" hint="Numer, pod którym dokument został zaksięgowany.">
        {(id) => (
          <Input
            id={id}
            value={ledgerRef}
            placeholder="np. ZK/2026/08/145"
            onChange={(event) => setLedgerRef(event.target.value)}
          />
        )}
      </Field>

      <Field
        label="Forma płatności"
        hint={
          paymentMethod === 'CASH'
            ? 'Faktura gotówkowa nie trafia do rozrachunków.'
            : undefined
        }
      >
        {(id) => (
          <Select
            id={id}
            value={paymentMethod}
            onChange={(event) => setPaymentMethod(event.target.value as PaymentMethod)}
          >
            {PAYMENT_METHODS.map((method) => (
              <option key={method} value={method}>
                {PAYMENT_METHOD_LABELS[method]}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {asksForPaidAt && (
        <Field label="Data zapłaty" hint="Zostaw puste, jeśli nie znasz daty. Uzupełnisz ją później.">
          {(id) => (
            <Input
              id={id}
              type="date"
              value={paidOn}
              onChange={(event) => setPaidOn(event.target.value)}
            />
          )}
        </Field>
      )}

      {save.error && <ErrorBox>{errorMessage(save.error)}</ErrorBox>}

      <Button
        variant="primary"
        disabled={!isDirty}
        isLoading={save.isPending}
        onClick={() =>
          void save.mutateAsync({
            id: invoice.id,
            input: {
              ledgerRef: ledgerRef.trim() || null,
              paymentMethod,
              paidAt: asksForPaidAt && paidOn ? new Date(paidOn) : null,
            },
          })
        }
      >
        Zapisz dane księgowe
      </Button>
    </div>
  );
}

/**
 * Deleting the duplicate that should never have been keyed in.
 *
 * Soft delete since 24.09.2026: the invoice moves to "Usunięte" with its scans
 * and history, out of every list and sum, and can be restored from there. The
 * confirmation stays — disappearing from the register is still a decision —
 * and names what happens instead of asking "na pewno?", which nobody reads.
 */
function DeleteInvoiceCard({ invoice }: { invoice: InvoiceDetail }) {
  const [isConfirming, setIsConfirming] = useState(false);
  const navigate = useNavigate();
  const remove = useDeleteInvoice();

  async function handleDelete() {
    await remove.mutateAsync(invoice.id);
    void navigate('/faktury', { replace: true });
  }

  return (
    <div className={styles.actionCard}>
      <p className={styles.actionTitle}>Usuń fakturę</p>
      <p className={styles.actionNote}>Dla omyłkowego lub zdublowanego wpisu.</p>

      <Button variant="danger" onClick={() => setIsConfirming(true)}>
        Usuń fakturę
      </Button>

      {isConfirming && (
        <Modal
          isOpen
          title={`Usunąć fakturę ${invoice.number}?`}
          onClose={() => setIsConfirming(false)}
          footer={
            <>
              <Button onClick={() => setIsConfirming(false)} disabled={remove.isPending}>
                Anuluj
              </Button>
              <span className={modalStyles.footerSpacer} />
              <Button
                variant="danger"
                isLoading={remove.isPending}
                onClick={() => void handleDelete()}
              >
                Usuń fakturę
              </Button>
            </>
          }
        >
          <p>
            Faktura od <b>{invoice.vendorName}</b> zniknie z rejestru, rozrachunków i zestawień.
            Skan i historia zostaną w „Usuniętych", skąd można ją przywrócić. Ten numer będzie
            można wpisać ponownie.
          </p>

          {remove.error && <ErrorBox>{errorMessage(remove.error)}</ErrorBox>}
        </Modal>
      )}
    </div>
  );
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Nie udało się zapisać zmiany.';
}
